import { readFile } from "node:fs/promises";
import { join } from "node:path";
import type { SessionPreviewPort } from "@project/shared";

export interface ConveyorConfig {
  setupCommand?: string;
  startCommand?: string;
  previewPort?: number;
}

export interface ForwardPortsResult {
  ports: number[];
  attributes: Record<string, { label?: string; visibility?: "public" | "private" }>;
}

const DEVCONTAINER_PATH = ".devcontainer/conveyor/devcontainer.json";

/** Ports bound by sidecars (postgres/redis/elasticsearch) — never expose. */
const DEVCONTAINER_PORT_DENY_LIST = new Set([5432, 6379, 9200]);

export async function loadForwardPorts(workspaceDir: string): Promise<ForwardPortsResult> {
  try {
    const raw = await readFile(join(workspaceDir, DEVCONTAINER_PATH), "utf-8");
    const parsed = JSON.parse(raw) as {
      forwardPorts?: number[];
      portsAttributes?: Record<string, { label?: string; visibility?: string }>;
    };
    const ports = (parsed.forwardPorts ?? []).filter(
      (p) => typeof p === "number" && !DEVCONTAINER_PORT_DENY_LIST.has(p),
    );
    const attributes: ForwardPortsResult["attributes"] = {};
    for (const [key, value] of Object.entries(parsed.portsAttributes ?? {})) {
      if (!value || typeof value !== "object") continue;
      const entry: { label?: string; visibility?: "public" | "private" } = {};
      if (typeof value.label === "string") entry.label = value.label;
      if (value.visibility === "public" || value.visibility === "private") {
        entry.visibility = value.visibility;
      }
      attributes[key] = entry;
    }
    return { ports, attributes };
  } catch {
    return { ports: [], attributes: {} };
  }
}

/** Merge forwardPorts + portsAttributes into a SessionPreviewPort[] the API
 *  can persist directly onto CodespaceSession.previewPorts. Deny-listed
 *  ports are filtered (defense-in-depth; authoritative filter lives API-side). */
export function buildSessionPreviewPorts(result: ForwardPortsResult): SessionPreviewPort[] {
  return result.ports
    .filter((port) => !DEVCONTAINER_PORT_DENY_LIST.has(port))
    .map((port) => {
      const attr = result.attributes[String(port)];
      const entry: SessionPreviewPort = { port };
      if (attr?.label) entry.label = attr.label;
      if (attr?.visibility) entry.visibility = attr.visibility;
      return entry;
    });
}

/** Load config from env vars (project-level settings injected via bootstrap). */
export function loadConveyorConfig(): ConveyorConfig | null {
  const envSetup = process.env.CONVEYOR_SETUP_COMMAND;
  const envStart = process.env.CONVEYOR_START_COMMAND;
  const envPort = process.env.CONVEYOR_PREVIEW_PORT;
  if (envSetup || envStart) {
    return {
      setupCommand: envSetup,
      startCommand: envStart,
      previewPort: envPort ? Number(envPort) : undefined,
    };
  }
  return null;
}
